import React, { useState, useEffect } from "react";
import './ViewEntity.css';
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import Drawer from "../Drawer";

const ViewEntity = ({
  id,
  entity,
  fetchFunction, 
  columns,
  data: initialFormData,
  loadingKey = 'viewDetails',
  loadingMessage = 'Loading details...'
}) => {
  const [formData, setFormData] = useState(initialFormData || {});
  const {
    loadingStates,
    loadingMessages,
    loadingProgress,
    withLoading
  } = useComponentLoading({
    [loadingKey]: false
  });

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerField, setDrawerField] = useState(null);

  useEffect(() => {
    console.log("Received initial data in ViewEntity:", initialFormData);
    setFormData(initialFormData);
  }, [initialFormData]);

  useEffect(() => {
    if (id && fetchFunction) {
      fetchData();
    }
  }, [fetchFunction, id]);

  const fetchData = async () => {
    try {
      await withLoading(loadingKey, async (updateProgress) => {
        updateProgress(10);
        
        const query = new URLSearchParams({
          [entity]: id,
        });
        
        updateProgress(30);
        
        const response = await fetchFunction(query);
        
        updateProgress(60);
        
        if (!response.ok) throw new Error('Network response was not ok');
        const json = await response.json();
        
        updateProgress(90);
        
        setFormData(json);
        
        updateProgress(100);
      }, loadingMessage, 30000);
    } catch (error) {
      console.log('Error fetching data:', error);
    }
  };

  if (loadingStates[loadingKey]) {
    return <LoadingSpinner 
      message={loadingMessages[loadingKey]}
      progress={loadingProgress[loadingKey]}
      showProgress={true} 
    />
  }

  // Handle row click
  const handleRowClick = (col) => {
    if (col.drawer) {
      setDrawerField(col);
      setDrawerOpen(true);
    }
  };

  // Helper to get the full row (formData)
  const getDrawerContent = () => {
    if (!drawerField) return null;
    const value = formData[drawerField.field];
    // If drawerContent is a function, call it with value and row
    if (typeof drawerField.drawerContent === 'function') {
      return drawerField.drawerContent(value, formData);
    }
    // If drawerContent is a React element/component, render it
    if (drawerField.drawerContent) {
      return drawerField.drawerContent;
    }
    // Default rendering
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {drawerField.headerName || drawerField.label}
        </div>
        <div style={{ fontSize: '1.1rem' }}>
          {typeof value === 'boolean'
            ? value ? "Yes" : "No"
            : drawerField.type === 'number'
              ? value || 0
              : value || 'N/A'}
        </div>
      </div>
    );
  };

  // Helper to get drawer width from drawerContent result
  const getDrawerWidth = () => {
    if (!drawerField) return undefined;
    // If drawerContent is a function, call it with value and row
    if (typeof drawerField.drawerContent === 'function') {
      const result = drawerField.drawerContent(formData[drawerField.field], formData);
      return result && result.props && result.props.drawerWidth;
    }
    // If drawerContent is a React element/component, check its props
    if (drawerField.drawerContent && drawerField.drawerContent.props) {
      return drawerField.drawerContent.props.drawerWidth;
    }
    return undefined;
  };

  return (
    <div className="details-container">
      {formData ? (
        <div className="details-form">
          {columns.map((col) => (
            <div
              className={`form-group${col.drawer ? ' clickable' : ''}`}
              key={col.field}
              onClick={() => handleRowClick(col)}
              style={col.drawer ? { cursor: 'pointer' } : {}}
            >
              <label htmlFor={col.field} className="form-label">
                {(col.headerName || col.label)}
                {col.info && (
                  <span className="info-icon" title={col.info}>i</span>
                )}
              </label>
              <div className="form-value">
                <span>
                  {typeof formData[col.field] === 'boolean'
                    ? formData[col.field] ? "Yes" : "No"
                    : col.type === 'number'
                      ? formData[col.field] || 0
                      : formData[col.field] || 'N/A'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>Loading...</p>
      )}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={drawerField ? (drawerField.headerName || drawerField.label) : ''}
        width={getDrawerWidth()}
      >
        {getDrawerContent()}
      </Drawer>
    </div>
  );
};

export default ViewEntity;